import * as React from 'react';
import { MessageCircle, Paperclip, Send, X } from 'lucide-react';

import { cn } from '../lib/utils';
import { Badge } from '../primitives/badge';
import { Button } from '../primitives/button';
import { Textarea } from '../primitives/textarea';
import { CommentReactions } from './comment-reactions';

/**
 * One discussion entry on a regulated record.
 */
export interface CommentEntry {
  id: string;
  author: string;
  /** ISO string or Date. */
  postedAt: string | Date;
  body: string;
  /** Whether this comment was a reply to another. Threaded chains
   *  are rendered with a light indent and a "replying to" badge. */
  replyToId?: string;
}

/**
 * One file attached to the record. The panel doesn't own storage —
 * uploads are surfaced via the `onUpload` callback which the consuming
 * view wires to its API + blob store of choice.
 */
export interface AttachmentEntry {
  id: string;
  fileName: string;
  /** Size in bytes — rendered as a friendly KB/MB suffix. */
  sizeBytes?: number;
  /** ISO string or Date. */
  uploadedAt: string | Date;
  uploadedBy?: string;
  /** Open URL (signed link, content-disposition route, etc). */
  href?: string;
  /** Optional content-type label. */
  contentType?: string;
}

export interface CommentsAndAttachmentsPanelProps {
  /** Records the panel is bound to (used in the empty-state copy). */
  recordLabel?: string;
  comments: CommentEntry[];
  attachments: AttachmentEntry[];
  /** Submit a new comment. The panel resets the input on success. */
  onAddComment: (body: string) => Promise<void> | void;
  /** Upload a file. Returns when the upload completes. */
  onUpload?: (file: File) => Promise<void> | void;
  /** Remove a comment (admin-only; omit to disable). */
  onDeleteComment?: (id: string) => Promise<void> | void;
  /** Remove an attachment (admin-only; omit to disable). */
  onDeleteAttachment?: (id: string) => Promise<void> | void;
  /** When true, show skeleton placeholders. */
  loading?: boolean;
  /** Disable input + actions while a mutation is in flight. */
  pending?: boolean;
  /**
   * When true, render the emoji-reaction strip under each comment (the
   * Slack/Teams "react" affordance), wired to the polymorphic
   * `/comments/{id}/reactions` endpoints. Off by default so legacy consumers
   * are unaffected; the cross-module {@link EntityCollaborationRail} turns it
   * on because its comments are real entity-comment rows the reaction
   * endpoints accept.
   */
  reactionsEnabled?: boolean;
  /** The current operator's user id, used to highlight the caller's own reactions. */
  currentUserId?: string;
  className?: string;
}

const formatPostedAt = (t: string | Date): string => {
  const d = t instanceof Date ? t : new Date(t);
  if (Number.isNaN(d.getTime())) return String(t);
  return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
};

const formatBytes = (n?: number): string => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Shared comments + attachments side panel for regulated workflows.
 * Used by PLM, Quality, EBR, Batch, Compliance, Procurement,
 * Maintenance, Sales, CRM, and Finance views per docs/todo.md P2.
 *
 * Layout:
 *   ┌─────────────────────────────────────┐
 *   │ Comments (count)         Add comment│
 *   │   ╭─────────────────────────────╮   │
 *   │   │ Comment 1                   │   │
 *   │   ╰─────────────────────────────╯   │
 *   ├─────────────────────────────────────┤
 *   │ Attachments (count)      [Upload]   │
 *   │   - file1.pdf  450 KB               │
 *   │   - file2.csv  12 KB                │
 *   └─────────────────────────────────────┘
 */
export function CommentsAndAttachmentsPanel({
  recordLabel,
  comments,
  attachments,
  onAddComment,
  onUpload,
  onDeleteComment,
  onDeleteAttachment,
  loading = false,
  pending = false,
  reactionsEnabled = false,
  currentUserId,
  className,
}: CommentsAndAttachmentsPanelProps) {
  const [draft, setDraft] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const submit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onAddComment(trimmed);
      setDraft('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !onUpload) return;
    setUploading(true);
    try {
      await onUpload(f);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const disabled = pending || submitting || uploading;

  return (
    <section
      className={cn(
        'flex flex-col rounded-md border border-border/60 bg-surface',
        className,
      )}
      data-testid="comments-and-attachments-panel"
    >
      <div className="flex items-center justify-between gap-3 border-b border-border/60 p-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <MessageCircle className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Comments
          <Badge variant="outline" className="ml-1 text-xs">
            {comments.length}
          </Badge>
        </h3>
      </div>

      <div className="flex flex-col gap-2 p-3" aria-busy={loading}>
        {loading ? (
          <div className="space-y-2" role="status" aria-label="Loading comments">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="h-12 rounded-md border border-border/40 bg-muted/30 animate-pulse"
              />
            ))}
          </div>
        ) : comments.length === 0 ? (
          <p
            className="text-xs text-muted-foreground"
            data-testid="comments-empty"
          >
            No comments yet{recordLabel ? ` on ${recordLabel}` : ''}.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="comments-list">
            {comments.map((c) => (
              <li
                key={c.id}
                className={cn(
                  'rounded-md border border-border/60 bg-surface-overlay p-2',
                  c.replyToId && 'ml-4 border-l-4 border-l-primary/60',
                )}
              >
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium">{c.author}</span>
                  <span className="text-muted-foreground">{formatPostedAt(c.postedAt)}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm">{c.body}</p>
                {reactionsEnabled && (
                  <CommentReactions commentId={c.id} currentUserId={currentUserId} />
                )}
                {onDeleteComment && (
                  <div className="mt-1 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteComment(c.id)}
                      aria-label={`Delete comment from ${c.author}`}
                      className="h-6 text-xs"
                      disabled={disabled}
                    >
                      <X className="h-3 w-3 mr-1" aria-hidden="true" /> Remove
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-2 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Add a comment${recordLabel ? ` on ${recordLabel}` : ''}…`}
            rows={2}
            disabled={disabled}
            aria-label="Add comment"
          />
          <div className="flex justify-end">
            <Button
              variant="default"
              size="sm"
              onClick={submit}
              disabled={disabled || draft.trim().length === 0}
            >
              <Send className="h-3 w-3 mr-1" aria-hidden="true" />
              {submitting ? 'Posting…' : 'Post'}
            </Button>
          </div>
        </div>
      </div>

      <div className="border-t border-border/60 p-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <Paperclip className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Attachments
            <Badge variant="outline" className="ml-1 text-xs">
              {attachments.length}
            </Badge>
          </h3>
          {onUpload && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFile}
                aria-label="Choose file to upload"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </>
          )}
        </div>

        {loading ? (
          <div className="mt-2 h-10 rounded-md border border-border/40 bg-muted/30 animate-pulse" />
        ) : attachments.length === 0 ? (
          <p
            className="mt-2 text-xs text-muted-foreground"
            data-testid="attachments-empty"
          >
            No attachments.
          </p>
        ) : (
          <ul className="mt-2 space-y-1" data-testid="attachments-list">
            {attachments.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border/60 bg-surface-overlay p-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  {a.href ? (
                    <a
                      href={a.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate font-medium hover:underline"
                    >
                      {a.fileName}
                    </a>
                  ) : (
                    <span className="truncate font-medium">{a.fileName}</span>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {formatBytes(a.sizeBytes)}
                    {a.contentType ? ` • ${a.contentType}` : ''}
                    {a.uploadedBy ? ` • ${a.uploadedBy}` : ''}
                    {` • ${formatPostedAt(a.uploadedAt)}`}
                  </div>
                </div>
                {onDeleteAttachment && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDeleteAttachment(a.id)}
                    aria-label={`Delete attachment ${a.fileName}`}
                    className="h-7 w-7"
                    disabled={disabled}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

CommentsAndAttachmentsPanel.displayName = 'CommentsAndAttachmentsPanel';
